import memoizeOne from 'memoize-one';
import { Region } from '~/Api/Analytics/Region';
import { Country } from '~/Api/Country';

import ActionTypes from './ActionTypes';
import RegionAvailability from './RegionAvailability';
import { SortBy } from '~/ERP/domain';
import { ErpStatsResponse } from 'com.cogvio.pricemonitor.api';

export interface State {
	request: Promise<any> | null;
	isFetching: boolean;
	regions: Region[] | null;
}

type Action =
	| { type: ActionTypes.request; request: Promise<any> }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			regions: Region[] | null;
	  };

export type BarSeriesValue = [string, number | null];

function createState(): State {
	return {
		request: null,
		isFetching: false,
		regions: null,
	};
}

export default function reducer(state: State = createState(), action: Action) {
	switch (action.type) {
		case ActionTypes.request:
			return {
				...state,
				request: action.request,
				isFetching: true,
				regions: null,
			};

		case ActionTypes.receive:
			if (state.request !== action.request) {
				return state;
			}

			return {
				...state,
				request: action.regions ? action.request : null,
				isFetching: false,
				regions: action.regions,
			};

		default:
			return state;
	}
}

export function selectAllCountries(state: State): Country[] | null {
	const codes: Set<string> = new Set();
	const countries = state.regions?.reduce((countries: Country[], region) => {
		const regionCountries = region.countries.filter((country) => !codes.has(country.alpha2));
		regionCountries.forEach((country) => codes.add(country.alpha2));
		countries.push(...regionCountries);
		return countries;
	}, []);

	if (countries) {
		sortCountries(countries);
	}

	return countries || null;
}

export const selectRegionAvailabilities = memoizeOne(
	(state: State, availability: { [key: string]: any } | null): RegionAvailability[] | null => {
		if (!state.regions || !availability) {
			return null;
		}

		function mapRegion(region: Region): RegionAvailability {
			const countries = region.countries.filter((country) => Boolean((availability || {})[country.alpha2]));
			sortCountries(countries);
			return new RegionAvailability(region, region.subRegions.map(mapRegion), countries);
		}

		return state.regions.map(mapRegion);
	}
);

type CountryLike = { alpha2: string };
const sortCountryACountryB = (a: CountryLike, b: CountryLike) => a.alpha2.localeCompare(b.alpha2);

export function sortCountries<T extends CountryLike>(countries: T[]) {
	return countries.sort((country, otherCountry) => sortCountryACountryB(country, otherCountry));
}

export function sortCountryEntities<T extends { country: CountryLike }>(entitiesWithCountry: T[]) {
	return entitiesWithCountry.sort((a, b) => sortCountryACountryB(a.country, b.country));
}

export function sortSeriesByValue(a: BarSeriesValue, b: BarSeriesValue) {
	return (a[1] ?? 0) - (b[1] ?? 0);
}

export function sortSeriesByName(a: BarSeriesValue, b: BarSeriesValue) {
	return a[0].localeCompare(b[0]);
}
